import { IconButton } from "@/components/form"
import Icon from "@/components/Icon"
import type { Filter } from "@/models"
import usePopups from "@/stores/popupsStore"
import { faFilterCircleXmark, faXmarkCircle } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import {
	Alert,
	ConfirmResult,
	defineComponent,
	inferProps,
	ShadowContainer,
	ToggleButton,
	type ReactiveComponent,
} from "vue-utils"
import { useEditingSearchState } from "../../state"
import AdvancedFilters from "./AdvancedFilters/AdvancedFilters"
import SimpleFilters from "./SimpleFilters"

interface Props {
	advanced: boolean
	onTypeChange(advanced: boolean): void
	onFilterEdited(filter: Filter): void
	onCreatingFilter(): void
	onClose(): void
}

const closeButtonStyles = css`
	color: var(--bs-secondary);
	border: none;
	padding: 0;
	margin: 0;
	outline: none;
	background: transparent;

	&:hover {
		color: #303030;
	}

	svg {
		height: 1.5rem;
	}
`

const SearchMarriageClaimFilterHeader: ReactiveComponent<Props> = (props) => {
	const state = useEditingSearchState()
	const popups = usePopups()

	async function handleClearFilters() {
		if (state.filters.length === 0) {
			void popups.showAlertPopup(() => <Alert title={"There are no filters to be cleared."} />)
		} else {
			const result = await popups.showConfirmPopup({
				content: () => <Alert title="Confirm Clear Filters">Are you sure you want to clear all filters?</Alert>,
				confirmText: "Yes",
			})
			if (result === ConfirmResult.Confirm) {
				state.clearFilters()
			}
		}
	}

	return () => (
		<ShadowContainer class="d-flex flex-column">
			<div class="d-flex align-items-center justify-content-between mb-2" style="height: 2.5rem">
				<h5 class="mb-0">Filters</h5>
				<button class={closeButtonStyles} type="button" title="Close Filters" onClick={props.onClose}>
					<Icon icon={faXmarkCircle} cursor="pointer" />
				</button>
			</div>
			<div class="d-flex justify-content-evenly align-items-center">
				<h6
					style={`width: 33%; text-align: center; margin-bottom: 0; color:${props.advanced ? `#727272` : `var(--bs-heading-color)`}`}
				>
					Simple
				</h6>
				<ToggleButton toggled={props.advanced} setToggled={(toggled) => props.onTypeChange(toggled)} />
				<h6
					style={`width: 33%; text-align: center; margin-bottom: 0; color:${!props.advanced ? `#727272` : `var(--bs-heading-color)`}`}
				>
					Advanced
				</h6>
			</div>
			<hr />
			<IconButton
				class="justify-content-center mb-2"
				color="danger"
				icon={faFilterCircleXmark}
				onClick={() => void handleClearFilters()}
				title="Clear Filters"
			>
				Clear Filters
			</IconButton>
			{props.advanced ? (
				<AdvancedFilters
					filters={state.fullSearchOptions.Filters}
					filterEdited={props.onFilterEdited}
					filterRemoved={(filter) => state.removeFilter(state.filters.indexOf(filter))}
					createFilter={props.onCreatingFilter}
				/>
			) : (
				<SimpleFilters />
			)}
		</ShadowContainer>
	)
}

export default defineComponent(SearchMarriageClaimFilterHeader, inferProps<Props>())
